// Warnings and errors to the API's /logs/ingest, batched: the Blazor app's ClientLogService, with the same app name,
// the same entry shape and the same bounds, so its entries read in LogDNA exactly as before.

import { config } from './config.ts';
import { getDeviceId, getSessionId } from './device.ts';
import { goesToOurApi, wafReady } from './waf.ts';

const PROJECT = 'Influencer';
const PLATFORM = 'Web';
const APP_VERSION = '2.0.0';
const MAX_BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 5000;
const QUEUE_HARD_CAP = 500;

// What one entry may carry. These MIRROR the server's ClientLogService, which truncates to exactly these, so cutting
// here drops nothing the backend would have kept; it only stops an unbounded stack trace travelling.
const MAX_MESSAGE_LENGTH = 16000;
const MAX_EXCEPTION_MESSAGE_LENGTH = 2000;
const MAX_STACK_TRACE_LENGTH = 16000;
const MAX_PROPERTY_VALUE_LENGTH = 4000;

const appName = config.environment === 'Local'
    ? `Lify ${PROJECT} ${PLATFORM} Local (dev)`
    : `Lify ${PROJECT} ${PLATFORM} ${config.environment}`;

const ingestUrl = config.mainApiUrl.replace(/\/$/, '') + '/logs/ingest';

type LogProperties = Record<string, unknown>;

interface LogEntry {
    timestampUtc: string;
    level: 'Warning' | 'Error';
    message: string;
    logger: string | undefined;
    exceptionType: string | undefined;
    exceptionMessage: string | undefined;
    stackTrace: string | undefined;
    properties: Record<string, string> | undefined;
}

let queue: LogEntry[] = [];
let flushing = false;

function truncate(value: unknown, max: number): string | undefined {
    if (value === null || value === undefined) return undefined;

    const text = String(value);

    return text.length <= max ? text : text.slice(0, max);
}

function boundProperties(properties: LogProperties | null | undefined): Record<string, string> | undefined {
    if (!properties) return undefined;

    const result: Record<string, string> = {};

    for (const [key, value] of Object.entries(properties)) {
        if (value === null || value === undefined || value === '') continue;
        result[key] = truncate(value, MAX_PROPERTY_VALUE_LENGTH)!;
    }

    return Object.keys(result).length ? result : undefined;
}

function enqueue(
    level: LogEntry['level'],
    message: string,
    { logger, error, properties }: { logger?: string; error?: Error; properties?: LogProperties | null } = {}
): void {
    if (queue.length >= QUEUE_HARD_CAP) return;

    queue.push({
        timestampUtc: new Date().toISOString(),
        level,
        message: truncate(message, MAX_MESSAGE_LENGTH) || '',
        logger,
        exceptionType: error ? error.name || 'Error' : undefined,
        exceptionMessage: error ? truncate(error.message, MAX_EXCEPTION_MESSAGE_LENGTH) : undefined,
        stackTrace: error ? truncate(error.stack, MAX_STACK_TRACE_LENGTH) : undefined,
        properties: boundProperties(properties)
    });

    if (queue.length >= MAX_BATCH_SIZE) void flush();
}

/**
 * Sends what is queued, twenty at a time. Fire and forget: a failed batch is dropped, never re-queued, so a server
 * that refuses logs cannot make the queue grow. It calls fetch itself rather than api(), which logs its own
 * failures - a failure to send logs must not become a log. keepalive lets the last batch leave with a closing page.
 */
export async function flush({ keepalive = false }: { keepalive?: boolean } = {}): Promise<void> {
    if (flushing || queue.length === 0) return;

    flushing = true;

    try {
        while (queue.length) {
            const entries = queue.splice(0, MAX_BATCH_SIZE);

            const body = JSON.stringify({
                appName,
                appVersion: APP_VERSION,
                platform: PLATFORM,
                deviceId: getDeviceId(),
                sessionId: getSessionId(),
                entries
            });

            // A closing page cannot wait for a token; its batch goes with whatever cookie there is.
            if (!keepalive) await wafReady();

            try {
                await fetch(ingestUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body,
                    credentials: goesToOurApi(ingestUrl) ? 'include' : 'same-origin',
                    keepalive
                });
            } catch {
                // Dropped: see above.
            }
        }
    } finally {
        flushing = false;
    }
}

export const log = {
    /** logger is the category, as ILogger named it in the Blazor app. */
    warn(message: string, properties?: LogProperties | null, logger?: string): void {
        console.warn(message, properties || '');
        enqueue('Warning', message, { logger, properties });
    },

    error(message: string, error?: unknown, properties?: LogProperties | null, logger?: string): void {
        console.error(message, error || '', properties || '');
        enqueue('Error', message, { logger, error: error instanceof Error ? error : undefined, properties });
    }
};

setInterval(() => void flush(), FLUSH_INTERVAL_MS);

// The batch still waiting when the tab is hidden or closed would otherwise be lost with it.
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush({ keepalive: true });
});
window.addEventListener('pagehide', () => void flush({ keepalive: true }));

window.addEventListener('error', event => {
    const properties: LogProperties = {};
    const stack = (event?.error as { stack?: unknown } | null | undefined)?.stack;

    if (event?.filename) properties['filename'] = event.filename;
    if (event?.lineno) properties['lineno'] = event.lineno;
    if (event?.colno) properties['colno'] = event.colno;
    if (stack) properties['stack'] = stack;

    enqueue('Error', `window.onerror: ${event?.message || 'Unknown error'}`, {
        logger: 'js.window.error',
        properties
    });
});

window.addEventListener('unhandledrejection', event => {
    const reason = (event ? (event.reason ?? event) : null) as { stack?: unknown; message?: unknown } | null;
    const properties: LogProperties = {};

    if (reason?.stack) properties['stack'] = reason.stack;

    enqueue('Error', `unhandledrejection: ${reason ? String(reason.message || reason) : 'unhandled rejection'}`, {
        logger: 'js.unhandledrejection',
        properties
    });
});
